import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Capacitor } from '@capacitor/core';

export type MotionPermissionStatus = 'unknown' | 'pending' | 'granted' | 'denied' | 'not_needed';

interface MotionPermissionContextType {
  motionPermission: MotionPermissionStatus;
  requestMotionPermission: () => Promise<boolean>;
  needsMotionPermission: boolean;
  isNative: boolean;
  showPermissionModal: boolean;
  setShowPermissionModal: (show: boolean) => void;
  motionEnabled: boolean;
  setMotionEnabled: (enabled: boolean) => void;
}

const MotionPermissionContext = createContext<MotionPermissionContextType | null>(null);

export function MotionPermissionProvider({ children }: { children: ReactNode }) {
  const isNative = Capacitor.isNativePlatform();

  // Vérifier si on a besoin de demander la permission (iOS 13+)
  // Wrapped in try-catch car DeviceOrientationEvent peut ne pas exister sur certains navigateurs
  const needsMotionPermission = (() => {
    try {
      return typeof window !== 'undefined' &&
             typeof DeviceOrientationEvent !== 'undefined' &&
             typeof (DeviceOrientationEvent as any).requestPermission === 'function';
    } catch {
      return false;
    }
  })();

  // État initial basé sur la plateforme
  // Sur iOS natif: toujours 'pending' au démarrage (permission par session)
  // Sur Android/Web: 'not_needed'
  const getInitialPermission = (): MotionPermissionStatus => {
    if (!isNative) return 'not_needed';
    if (!needsMotionPermission) return 'not_needed'; // Android
    return 'pending'; // iOS - toujours pending au démarrage
  };

  const getInitialMotionEnabled = (): boolean => {
    const saved = localStorage.getItem('lilhero_motion_enabled');
    return saved === null ? true : saved === 'true';
  };

  const [motionPermission, setMotionPermission] = useState<MotionPermissionStatus>(getInitialPermission);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [motionEnabled, setMotionEnabledState] = useState(getInitialMotionEnabled);

  // Afficher le modal au démarrage sur iOS si les effets motion sont activés
  useEffect(() => {
    if (isNative && needsMotionPermission && motionPermission === 'pending' && motionEnabled) {
      // Petit délai pour laisser l'app se charger
      const timer = setTimeout(() => {
        setShowPermissionModal(true);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [isNative, needsMotionPermission, motionPermission, motionEnabled]);

  // Sauvegarder la préférence motion enabled
  const setMotionEnabled = useCallback((enabled: boolean) => {
    setMotionEnabledState(enabled);
    localStorage.setItem('lilhero_motion_enabled', enabled.toString());
  }, []);

  // Fonction pour demander la permission (doit être appelée depuis un geste utilisateur)
  const requestMotionPermission = useCallback(async (): Promise<boolean> => {
    if (!needsMotionPermission) {
      setMotionPermission('not_needed');
      return true;
    }

    try {
      const permission = await (DeviceOrientationEvent as any).requestPermission();
      if (permission === 'granted') {
        setMotionPermission('granted');
        return true;
      } else {
        setMotionPermission('denied');
        return false;
      }
    } catch (err) {
      console.warn('Motion permission request failed:', err);
      setMotionPermission('denied');
      return false;
    }
  }, [needsMotionPermission]);

  return (
    <MotionPermissionContext.Provider
      value={{
        motionPermission,
        requestMotionPermission,
        needsMotionPermission,
        isNative,
        showPermissionModal,
        setShowPermissionModal,
        motionEnabled,
        setMotionEnabled
      }}
    >
      {children}
    </MotionPermissionContext.Provider>
  );
}

export function useMotionPermission() {
  const context = useContext(MotionPermissionContext);
  if (!context) {
    throw new Error('useMotionPermission must be used within a MotionPermissionProvider');
  }
  return context;
}
